import { useLayoutEffect } from 'react';
import { remPx } from '../../lib/rem';
import { paneHeightPx } from './paneHeight';

/**
 * Keeps `--composer-pane-h` on `row` - the grid the Media Library pane sits in - at the height the
 * pane may take and still be fully on screen the moment it opens (paneHeight.ts). Measured, not
 * assumed: whatever is drawn above the row - the test-mode banner, the destination strip, a notice,
 * a row that wrapped - comes off it, and so does the bar pinned over the bottom of the scroll
 * container (`[data-composer-bar]`, the composer's "Save and close" bar), at the height it has.
 *
 * Measured against the nearest `[data-composer-scroll]`, the composer modal's scroll container, and
 * measured again whenever that or its `[data-composer-content]` changes size - the bar is inside
 * the content, so a bar that appears, goes or changes height is seen as well. With no such ancestor
 * (a composer outside the modal) or no ResizeObserver, nothing is set and the pane's own CSS
 * fallback stands.
 *
 * The height written cannot change what it was measured from. It sizes the pane, which is inside the
 * row: the scrollport's height is the modal's, where the row starts depends on what is above it,
 * and the bar is outside the row and as tall as its own buttons. So the measurement that follows a
 * write reads the same three numbers and writes nothing.
 *
 * `row` is the element itself, held in state by the caller: the grid is drawn on the Content tab
 * only, so it comes and goes while the composer stays.
 */
export function useComposerPaneHeight(row: HTMLElement | null): void {
  useLayoutEffect(() => {
    if (!row || typeof ResizeObserver === 'undefined') return;
    const scroller = row.closest<HTMLElement>('[data-composer-scroll]');
    if (!scroller) return;

    let written: number | null = null;
    const measure = () => {
      // Where the row starts with nothing scrolled, whatever is scrolled now.
      const restTop =
        row.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      // Looked for each time: the bar is the composer's, and need not be there.
      const bar = scroller.querySelector<HTMLElement>('[data-composer-bar]');
      const barHeight = bar ? bar.getBoundingClientRect().height : 0;
      const next = paneHeightPx(scroller.clientHeight, restTop, barHeight, remPx());
      if (next === written) return;
      written = next;
      row.style.setProperty('--composer-pane-h', `${next}px`);
    };
    measure();

    // Written in the next frame, never inside the observer's own callback: a size written there
    // raises the browser's "ResizeObserver loop" error.
    let frame: number | null = null;
    const observer = new ResizeObserver(() => {
      if (frame != null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        measure();
      });
    });
    observer.observe(scroller);
    const content = scroller.querySelector('[data-composer-content]');
    if (content) observer.observe(content);

    return () => {
      observer.disconnect();
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, [row]);
}
